import { useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import {
  csvExporter,
  handleFilterChange,
  xlsxExporter,
} from "../../../../helpers";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import * as XLSX from "xlsx";
import {
  exportReports,
  orderReportList,
} from "../../../../services/eventReportService";
import ExportDropdown from "../EventReport/ExportDropdown";
import Button from "../../../form/Button";
import DatePicker from "../../../form/DatePicker";
import Badge from "../../../elements/Badge";
import { eventCountList } from "../../../../services/eventService";
import { organizationTeamItem } from "../../../../services/organizationService";
import { transactionList } from "../../../../services/orderService";
import { formatEventDate } from "../../../../util/Flag";
import { useTranslation } from "react-i18next";

const getTicketTypeStyle = (type) => {
  const typeLower = type?.toLowerCase();

  const typeStyles = {
    ticket: {
      backgroundColor: "#EDE9FE", // Purple background
      color: "#7C3AED", // Purple text
      borderColor: "#8B5CF6", // Purple border
    },
    "add-on": {
      backgroundColor: "#ECFDF5", // Emerald background
      color: "#059669", // Emerald text
      borderColor: "#10B981", // Emerald border
    },
  };

  return (
    typeStyles[typeLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const AddOnsList = ({ selectedEvents = [], setDrawer = () => {} }) => {
  const { t } = useTranslation();
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Fetch orders data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "addons-reports",
      filters,
      search,
      selectedEvents?.map((item) => item?._id),
    ],
    queryFn: async () => {
      const res = await transactionList({
        ...filters,
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        viewAs: "reporting",
        ticketType: "addons-event",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return selectedEvents?.map((item) => item?._id)?.length > 0
        ? res?.docs
        : {};
    },
  });

  // Handle export functionality with client-side download and API call
  const handleExport = async (format) => {
    setIsExportOpen(false);
    toast.info("Preparing export data...");
    try {
      const exportResponse = await transactionList({
        ...filters,
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        viewAs: "reporting",
        limit: data?.metadata?.totalDocs, // Large number to get all records
        page: 1,
        ticketType: "addons-event",
      });

      if (exportResponse?.docs?.status !== 200) {
        toast.error(exportResponse?.message || "Failed to fetch export data");
        return;
      }

      const orderData = exportResponse?.docs?.data || [];

      if (!orderData || orderData.length === 0) {
        toast.warning("No data available to export");
        return;
      }
      // Transform the order data for export
      const exportData = orderData.map((order) => {
        const attendee_name = order?.attendeeData
          ?.filter((attendee) =>
            ["First Name", "Last Name"].includes(attendee?.question)
          )
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");
        const attendee_email = order?.attendeeData
          ?.filter((attendee) => ["Email"].includes(attendee?.question))
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");
        return {
          "Attendee Name": attendee_name || "-",
          Email: attendee_email || "-",
          "Event Name": lngDetect(order?.orderData?.eventData?.title) || "-",
          "Ticket Name": lngDetect(order?.ticketInfo?.name) || "-",
          "Ticket Type": order?.ticketInfo?.type
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        };
      });

      if (format === "csv") {
        csvExporter(exportData, "add-ons").then(async (result) => {
          if (result?.status === 200) {
            const resp = await exportReports({
              name: result?.fileName,
              file: result?.file,
              type: result?.fileCategory,
              status: "success",
              organization: selectedOrganization,
            });
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      } else if (format === "xlsx") {
        xlsxExporter(exportData, "add-ons").then(async (result) => {
          if (result?.status === 200) {
            const resp = await exportReports({
              name: result?.fileName,
              file: result?.file,
              type: result?.fileCategory,
              status: "success",
              organization: selectedOrganization,
            });
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExportOpen(false);
    }
  };

  const tableHeadData = [
    {
      _id: "attendeeName",
      name: t("Attendee Name"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Email"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Ticket Name"),
      align: "left",
    },
    {
      _id: "ticketType",
      name: t("Ticket Type"),
      align: "left",
    },
  ];

  // Table Body Data
  const tableData = data?.data?.map((item) => {
    // Ticket type styling
    const ticketType = item?.ticketInfo?.type || "standard";
    const ticketTypeText = ticketType
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const ticketTypeStyle = getTicketTypeStyle(ticketType);
    const attendee_name = item?.attendeeData
      ?.filter((attendee) =>
        ["First Name", "Last Name"].includes(attendee?.question)
      )
      ?.map((attendee) => `${attendee?.answers || ""}`.trim())
      .join(" ");
    const attendee_email = item?.attendeeData
      ?.filter((attendee) => ["Email"].includes(attendee?.question))
      ?.map((attendee) => `${attendee?.answers || ""}`.trim())
      .join(" ");

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px] text-purple-600 font-medium",
          data: attendee_name || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[200px] text-gray-600",
          data: attendee_email || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px] text-purple-600",
          data: lngDetect(item?.orderData?.eventData?.title) || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px] text-purple-600",
          data: lngDetect(item?.ticketInfo?.name) || "-",
        },
        {
          _id: "ticketType",
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
              style={{
                backgroundColor: ticketTypeStyle.backgroundColor,
                color: ticketTypeStyle.color,
                borderColor: ticketTypeStyle.borderColor,
              }}
            >
              {ticketTypeText}
            </span>
          ),
        },
      ],
    };
  });

  const { data: eventCount, isLoading: eventCountLoading } = useQuery({
    queryKey: ["event-count-data", selectedEvents?.map((item) => item?._id)],
    queryFn: async () => {
      const res = await eventCountList({
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        sortQuery: "-createdAt",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={t("Search here")}
              divClasses={"mb-0"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <Button
            buttonIcon={"fa-light fa-calendar-days"}
            buttonLabel={`${t("Select Events")} (${
              selectedEvents?.length || 0
            })`}
            buttonFunction={() => {
              setDrawer({ isOpen: true, data: null });
            }}
          />
          <ExportDropdown
            onExport={handleExport}
            isOpen={isExportOpen}
            onToggle={() => setIsExportOpen(!isExportOpen)}
          />
        </div>
      </div>

      {/* Orders Table */}
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(
            ["order", filters, search, selectedOrganization],
            (state) => {
              if (state) {
                return { ...state, data: val };
              }
            }
          );
        }}
        // handleOrdering={(newData, oldData) =>
        //   handleOrdering({ newData, oldData })
        // }
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />
    </div>
  );
};

export default AddOnsList;
